import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  IconButton,
  Text,
  Center,
  Spinner,
  useColorModeValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Badge,
  HStack,
  Avatar,
  Grid,
  GridItem,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
} from '@chakra-ui/react';
import { FaTrash, FaStar, FaStore, FaShoppingBag } from 'react-icons/fa';
import axios from 'axios';
import { platform_reviews_url, all_product_reviews_url, delete_product_review_url } from '../utils/constants';
import { getSecureUrl } from '../utils/helpers';
import { SidebarWithHeader } from '../components';

const PlatformReviewsPage = () => {
  const [platformReviews, setPlatformReviews] = useState([]);
  const [productReviews, setProductReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [productIdForDelete, setProductIdForDelete] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const cancelRef = React.useRef();
  const toast = useToast();
  const tableBg = useColorModeValue('white', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.700');

  const fetchPlatformReviews = async () => {
    try {
      const response = await axios.get(platform_reviews_url);
      if (response.data.success) {
        const reviews = (response.data.reviews || []).map((r) => {
          const sanitized = { ...r };
          Object.keys(sanitized).forEach((k) => {
            if (typeof sanitized[k] === 'string' && sanitized[k].startsWith('http://')) {
              sanitized[k] = getSecureUrl(sanitized[k]);
            }
          });
          return sanitized;
        });
        setPlatformReviews(reviews);
      }
    } catch (error) {
      console.error('Error fetching platform reviews:', error);
    }
  };

  const fetchProductReviews = async () => {
    try {
      const response = await axios.get(all_product_reviews_url);
      if (response.data.success) {
        setProductReviews(response.data.reviews);
      }
    } catch (error) {
      console.error('Error fetching product reviews:', error);
    }
  };

  const fetchAllReviews = async () => {
    setLoading(true);
    await Promise.all([fetchPlatformReviews(), fetchProductReviews()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAllReviews();
    // eslint-disable-next-line
  }, []);

  const handleDeleteClick = (id, type, productId = null) => {
    setDeleteId(id);
    setDeleteType(type);
    setProductIdForDelete(productId);
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      if (deleteType === 'platform') {
        await axios.delete(`${platform_reviews_url}/${deleteId}`, {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
          },
        });
      } else {
        await axios.delete(`${delete_product_review_url}/${productIdForDelete}`, {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
          },
          data: { reviewId: deleteId },
        });
      }
      toast({
        position: 'top',
        description: 'Review deleted successfully',
        status: 'success',
        duration: 5000,
      });
      fetchAllReviews();
    } catch (error) {
      toast({
        position: 'top',
        description: error.response?.data?.message || 'Error deleting review',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setDeleting(false);
      setIsDeleteOpen(false);
      setDeleteId(null);
      setDeleteType(null);
      setProductIdForDelete(null);
    }
  };

  const renderStars = (rating) => {
    return (
      <HStack spacing={1}>
        {[...Array(5)].map((_, i) => (
          <FaStar
            key={i}
            color={i < rating ? '#ffc107' : '#e0e0e0'}
            size={12}
          />
        ))}
      </HStack>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const calculateAverageRating = (reviews) => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  if (loading) {
    return (
      <SidebarWithHeader>
        <Center h="400px">
          <Spinner size="xl" color="brown.500" />
        </Center>
      </SidebarWithHeader>
    );
  }

  return (
    <SidebarWithHeader>
      <Box p={4}>
        <Heading mb={6} fontSize="2xl">
          Reviews Management
        </Heading>

        {/* Two Column Layout for Reviews */}
        <Grid templateColumns={{ base: '1fr', xl: 'repeat(2, 1fr)' }} gap={6}>
          {/* Platform Reviews */}
          <GridItem>
            <Box>
              {/* Platform Stats Card */}
              <Box bg={cardBg} p={4} borderRadius="lg" shadow="sm" borderLeft="4px solid" borderLeftColor="teal.500" mb={4}>
                <Stat>
                  <StatLabel color="gray.500">Platform Reviews</StatLabel>
                  <StatNumber>{platformReviews.length}</StatNumber>
                  <StatHelpText>
                    <HStack>
                      <Icon as={FaStore} color="teal.500" />
                      <Text>Avg: {calculateAverageRating(platformReviews)} ⭐</Text>
                    </HStack>
                  </StatHelpText>
                </Stat>
              </Box>

              <Flex align="center" mb={4}>
                <Icon as={FaStore} color="teal.500" mr={2} />
                <Heading size="md">Platform Reviews</Heading>
                <Badge ml={2} colorScheme="teal">{platformReviews.length}</Badge>
              </Flex>
              
              {platformReviews.length === 0 ? (
                <Box bg={tableBg} borderRadius="lg" p={8} textAlign="center">
                  <Text color="gray.500">No platform reviews yet</Text>
                </Box>
              ) : (
                <TableContainer bg={tableBg} borderRadius="lg" shadow="sm" maxH="600px" overflowY="auto">
                  <Table variant="simple" size="sm">
                    <Thead position="sticky" top={0} bg={tableBg} zIndex={1}>
                      <Tr>
                        <Th>Customer</Th>
                        <Th>Rating</Th>
                        <Th>Comment</Th>
                        <Th>Date</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {platformReviews.map((review) => (
                        <Tr key={review._id}>
                          <Td>
                            <HStack>
                              <Avatar size="xs" name={review.name} bg="teal.500" />
                              <Box>
                                <Text fontWeight="medium" fontSize="sm">{review.name}</Text>
                                <Text fontSize="xs" color="gray.500">{review.email}</Text>
                              </Box>
                            </HStack>
                          </Td>
                          <Td>
                            <HStack>
                              {renderStars(review.rating)}
                              <Badge colorScheme="yellow" size="sm">{review.rating}</Badge>
                            </HStack>
                          </Td>
                          <Td maxW="200px">
                            <Text noOfLines={2} fontSize="sm">{review.comment}</Text>
                          </Td>
                          <Td>
                            <Text fontSize="xs" color="gray.500">{formatDate(review.createdAt)}</Text>
                          </Td>
                          <Td>
                            <IconButton
                              icon={<FaTrash />}
                              colorScheme="red"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(review._id, 'platform')}
                              aria-label="Delete review"
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </GridItem>

          {/* Product Reviews */}
          <GridItem>
            <Box>
              {/* Product Stats Card */}
              <Box bg={cardBg} p={4} borderRadius="lg" shadow="sm" borderLeft="4px solid" borderLeftColor="purple.500" mb={4}>
                <Stat>
                  <StatLabel color="gray.500">Product Reviews</StatLabel>
                  <StatNumber>{productReviews.length}</StatNumber>
                  <StatHelpText>
                    <HStack>
                      <Icon as={FaShoppingBag} color="purple.500" />
                      <Text>Avg: {calculateAverageRating(productReviews)} ⭐</Text>
                    </HStack>
                  </StatHelpText>
                </Stat>
              </Box>

              <Flex align="center" mb={4}>
                <Icon as={FaShoppingBag} color="purple.500" mr={2} />
                <Heading size="md">Product Reviews</Heading>
                <Badge ml={2} colorScheme="purple">{productReviews.length}</Badge>
              </Flex>
              
              {productReviews.length === 0 ? (
                <Box bg={tableBg} borderRadius="lg" p={8} textAlign="center">
                  <Text color="gray.500">No product reviews yet</Text>
                </Box>
              ) : (
                <TableContainer bg={tableBg} borderRadius="lg" shadow="sm" maxH="600px" overflowY="auto">
                  <Table variant="simple" size="sm">
                    <Thead position="sticky" top={0} bg={tableBg} zIndex={1}>
                      <Tr>
                        <Th>Customer</Th>
                        <Th>Product</Th>
                        <Th>Rating</Th>
                        <Th>Comment</Th>
                        <Th>Date</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {productReviews.map((review) => (
                        <Tr key={review._id}>
                          <Td>
                            <HStack>
                              <Avatar size="xs" name={review.name} bg="purple.500" />
                              <Box>
                                <Text fontWeight="medium" fontSize="sm">{review.name}</Text>
                                <Text fontSize="xs" color="gray.500">{review.email}</Text>
                              </Box>
                            </HStack>
                          </Td>
                          <Td maxW="120px">
                            <Text noOfLines={1} fontSize="sm" fontWeight="medium" color="purple.600">
                              {review.productName}
                            </Text>
                          </Td>
                          <Td>
                            <HStack>
                              {renderStars(review.rating)}
                              <Badge colorScheme="yellow" size="sm">{review.rating}</Badge>
                            </HStack>
                          </Td>
                          <Td maxW="150px">
                            <Text noOfLines={2} fontSize="sm">{review.comment}</Text>
                          </Td>
                          <Td>
                            <Text fontSize="xs" color="gray.500">{formatDate(review.createdAt)}</Text>
                          </Td>
                          <Td>
                            <IconButton
                              icon={<FaTrash />}
                              colorScheme="red"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(review._id, 'product', review.productId)}
                              aria-label="Delete review"
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </GridItem>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          isOpen={isDeleteOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => setIsDeleteOpen(false)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete {deleteType === 'platform' ? 'Platform' : 'Product'} Review
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to delete this review? This action cannot be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setIsDeleteOpen(false)}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleDeleteConfirm}
                  ml={3}
                  isLoading={deleting}
                >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    </SidebarWithHeader>
  );
};

export default PlatformReviewsPage;
